// ============================================================================
// COMBINED TRANSACTION HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { combinedService } from 'api/services/combinedService';
import type { CombinedTransaction, CreateCombinedPayload, UpdateCombinedPayload } from 'api/types/combined.types';
import { notifyDataChanged, useDataChangeListener } from './useDataSync';

const DATA_KEY = 'combined';

export const useCombined = () => {
  const [data, setData] = useState<CombinedTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCombined = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await combinedService.getAll({ pageSize: 50000 });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCombined();
  }, [fetchCombined]);

  useDataChangeListener(DATA_KEY, fetchCombined);

  return { data, loading, error, refetch: fetchCombined };
};

export const useCreateCombined = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createCombined = useCallback(async (payload: CreateCombinedPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await combinedService.create(payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createCombined, loading, error };
};

export const useUpdateCombined = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateCombined = useCallback(async (id: string, payload: UpdateCombinedPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await combinedService.update(id, payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateCombined, loading, error };
};

export const useSubmitCombined = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submitCombined = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await combinedService.submit(id);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submitCombined, loading, error };
};

export const useDeleteCombined = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteCombined = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await combinedService.delete(id);
      notifyDataChanged(DATA_KEY);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteCombined, loading, error };
};
